/**
 * 表の並べ替え。数値は丸める前の値（10進の文字列）で比べ、値のない行は、並べ替えの向きによらず最後に並べる。
 * 同じ値の行は、並べ替える前の順を保つ。
 */

import { compareDecimal } from '@/lib/credit-consumption/decimal';

export type SortDir = 'asc' | 'desc';

export interface SortState {
  key: string;
  dir: SortDir;
}

/** 並べ替えに使う値。`code` は文字のコードポイントの順、`text` は日本語の辞書の順 */
export type SortValue =
  | { kind: 'decimal'; value: string | null }
  | { kind: 'code'; value: string | null }
  | { kind: 'text'; value: string | null };

const collator = new Intl.Collator('ja');

function compareValues(a: SortValue, b: SortValue): number {
  const x = a.value as string;
  const y = b.value as string;
  switch (a.kind) {
    case 'decimal':
      return compareDecimal(x, y);
    case 'code':
      return x < y ? -1 : x > y ? 1 : 0;
    case 'text':
      return collator.compare(x, y);
  }
}

export function sortRows<T>(rows: T[], value: (row: T) => SortValue, dir: SortDir): T[] {
  return rows
    .map((row, index) => ({ row, index, v: value(row) }))
    .sort((a, b) => {
      const an = a.v.value === null;
      const bn = b.v.value === null;
      if (an || bn) return an === bn ? a.index - b.index : an ? 1 : -1;
      const c = compareValues(a.v, b.v);
      return c !== 0 ? (dir === 'asc' ? c : -c) : a.index - b.index;
    })
    .map((x) => x.row);
}

/**
 * 見出しを押したときの次の並べ替え。同じ列なら向きを変える。
 * ほかの列は、数値の列なら大きい順（降順）から、文字の列なら昇順から始める
 */
export function nextSort(current: SortState | null, key: string, numeric: boolean): SortState {
  return current?.key === key
    ? { key, dir: current.dir === 'asc' ? 'desc' : 'asc' }
    : { key, dir: numeric ? 'desc' : 'asc' };
}
